/**
 * useTrackPageView — fires a `POST /api/track/visit` ping on each route change,
 * plus a heartbeat every 30 s so the admin sees LIVE visitor counts.
 *
 * Respects tab visibility (pauses heartbeat when tab is hidden) and skips
 * admin routes so the site owner doesn't inflate stats.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const isMobile = () =>
  typeof navigator !== "undefined" &&
  /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const lastSent = { path: null, t: 0 };
const HEARTBEAT_MS = 30_000;

// One shared heartbeat timer across all component mounts.
let heartbeatTimer = null;
let heartbeatStarted = false;

function startHeartbeat(getPage) {
  if (heartbeatStarted) return;
  heartbeatStarted = true;

  const send = () => {
    if (typeof document !== "undefined" && document.hidden) return;
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${apiBase}/api/track/heartbeat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        page: getPage(),
        referrer: null,
        is_mobile: isMobile(),
      }),
      keepalive: true,
    }).catch(() => {});
  };

  // Fire once immediately, then every HEARTBEAT_MS.
  send();
  heartbeatTimer = setInterval(send, HEARTBEAT_MS);
  // On tab focus, send an immediate ping so the count is fresh.
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) send();
    });
  }
}

export default function useTrackPageView() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    // Skip admin routes (don't count own visits)
    if (pathname.startsWith("/admin")) return;
    const page = pathname + (search || "");
    // De-dupe identical pings within 1s (React StrictMode double-mounts)
    const now = Date.now();
    if (lastSent.path === page && now - lastSent.t < 1000) return;
    lastSent.path = page;
    lastSent.t = now;

    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${apiBase}/api/track/visit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        page,
        referrer: document.referrer || null,
        is_mobile: isMobile(),
      }),
      keepalive: true,
    }).catch(() => {
      // silently ignore tracking errors
    });

    // Start the shared heartbeat once we know the user is on a public page.
    startHeartbeat(() => (window.location.pathname || "/") + (window.location.search || ""));
  }, [pathname, search]);
}
